import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import * as Icons from "lucide-react";
import { ArrowUpRight, Check, Hammer } from "lucide-react";
import { Seo } from "@/components/Seo";
import { Section, SectionHeader } from "@/components/Section";
import { SERVICES } from "@/lib/site";

export default function Services() {
  return (
    <div data-testid="services-page">
      <Seo
        title="Services"
        description="Eight in-house crafts: tile work, bathroom and kitchen renovations, painting, flooring, electrical, drywall and carpentry. Durham Region & GTA."
      />
      <Section>
        <SectionHeader
          overline="Services"
          title="Eight crafts, one accountable crew."
          description="Every trade you need to renovate a home — staffed by in-house specialists, not subcontractors juggling other jobs."
        />

        <div className="mt-16 flex flex-col">
          {SERVICES.map((s, idx) => {
            const Icon = Icons[s.icon] || Hammer;
            const reverse = idx % 2 === 1;
            return (
              <motion.div
                key={s.slug}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.5 }}
                className={`grid grid-cols-12 gap-6 md:gap-10 py-12 md:py-16 border-t border-zinc-200 ${
                  idx === SERVICES.length - 1 ? "border-b" : ""
                }`}
                data-testid={`service-row-${s.slug}`}
              >
                <div className={`col-span-12 md:col-span-5 ${reverse ? "md:order-2" : ""}`}>
                  <div className="aspect-[4/3] overflow-hidden border border-zinc-200">
                    <img
                      src={s.image}
                      alt={s.title}
                      className="w-full h-full object-cover hover:scale-105 transition-transform duration-700"
                    />
                  </div>
                </div>
                <div className={`col-span-12 md:col-span-7 flex flex-col justify-center ${reverse ? "md:order-1 md:pr-8" : "md:pl-8"}`}>
                  <div className="flex items-center gap-3 mb-4">
                    <span className="text-xs font-bold text-orange-600">
                      {String(idx + 1).padStart(2, "0")} / {String(SERVICES.length).padStart(2, "0")}
                    </span>
                    <Icon className="w-5 h-5 text-zinc-900" />
                  </div>
                  <h3 className="font-display text-3xl md:text-4xl lg:text-5xl tracking-tighter leading-[1.02] text-zinc-950">
                    {s.title}
                  </h3>
                  <p className="mt-4 text-base md:text-lg text-zinc-600 leading-relaxed max-w-xl">
                    {s.summary}
                  </p>
                  <ul className="mt-6 space-y-2 max-w-md">
                    {s.details.map((d) => (
                      <li key={d} className="flex items-start gap-3 text-zinc-700">
                        <Check className="w-4 h-4 text-orange-600 mt-1 shrink-0" />
                        <span>{d}</span>
                      </li>
                    ))}
                  </ul>
                  <div className="mt-7">
                    <Link
                      to="/contact"
                      className="inline-flex items-center gap-2 text-sm font-bold uppercase tracking-wider text-zinc-900 border-b-2 border-orange-600 pb-1 hover:text-orange-600 transition"
                      data-testid={`service-cta-${s.slug}`}
                    >
                      Request a quote <ArrowUpRight className="w-4 h-4" />
                    </Link>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      </Section>
    </div>
  );
}
